import React from 'react'
import cardValidator from 'card-validator'
import { Formik } from 'formik'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { prettyCardNumber } from '../../utils/creditCard'
import { tokenizeCard } from '../../service/payment/payment'
import CreditCardIcon from '../creditCardIcon/CreditCardIcon'
import Field from '../field'
import Spinner from '../spinner'
import { urlToObject } from '../../utils/url'
import Button from '../button'
import { TEST_CARD } from './constants'

import cvvToolTip from '../../images/tooltips/cvv.png'
import cidToolTip from '../../images/tooltips/cid.png'
import zipToolTip from '../../images/tooltips/zip.png'
import cvvToolTipArabic from '../../images/tooltips/cvvAr.png'
import zipToolTipArabic from '../../images/tooltips/zipAr.png'
import lockIcon from './lock.svg'
import './addCardForm.sass'

// NOTE: test cards:
//       https://developers.braintreepayments.com/guides/credit-cards/testing-go-live/node
//       https://docs.spreedly.com/reference/test-data/
//       Test card work in payment of non-US flights and price < 2000$

// TODO: add filling of test data
// NOTE: that component render in iframe and communicate throught postMessage

class AddCardForm extends React.Component {
  static propTypes = {
    onTokenize: PropTypes.func.isRequired,
    onError: PropTypes.func,
    isLoading: PropTypes.bool,
    submitError: PropTypes.string,
    withZIP: PropTypes.bool,
    isArabic: PropTypes.bool,
  }
  static defaultProps = {
    onError: null,
    isLoading: false,
    submitError: null,
    withZIP: false,
    isArabic: false,
  }
  constructor(props) {
    super(props)
    this.form = React.createRef()
    this.fields = {
      number: {
        placeholder: this.props.isArabic ? 'رقم البطاقة' : 'Card number',
        label: this.props.isArabic ? 'رقم البطاقة' : 'Card number',
        mask: value => {
          const matches = value.match(/\d+/g)
          value = matches ? matches.join('') : ''
          const cardType = cardValidator.number(value)

          return cardType.card ? prettyCardNumber(value.replace(/ /g, ''), cardType.card) : value
        },
      },
      cardHolder: {
        placeholder: this.props.isArabic ? 'اسم حامل البطاقة' : 'Cardholder name',
        label: this.props.isArabic ? 'اسم حامل البطاقة' : 'Cardholder name'
      },
      expirationMonth: {
        placeholder: this.props.isArabic ? 'الشهر/العام' : 'MM / YY',
        label: this.props.isArabic ? 'الشهر/العام' : 'MM / YY',
        maxLength: 2,
        mask: 'number',
        pair: {
          name: 'expirationYear',
          maxLength: 2,
          mask: 'number',
          divider: '/',
        },
        className: this.props.withZIP ? 'field-third' : 'field-half',
      },
      cvv: {
        placeholder: this.props.isArabic ? 'رمز CVV' : 'CVV',
        type: 'password',
        label: this.props.isArabic ? 'رمز CVV' : 'CVV',
        maxLength: 3,
        mask: 'number',
        toolTip: this.props.isArabic ? cvvToolTipArabic : cvvToolTip,
        className: this.props.withZIP ? 'field-third' : 'field-half',
      },
      ...this.props.withZIP && {
        postalCode: {
          placeholder: 'ZIP',
          label: 'ZIP',
          autocomplete: false,
          toolTip: this.props.isArabic ? zipToolTipArabic : zipToolTip,
          className: 'field-third'
        },
      },
    }
  }


  state = {
    errorMessage: null,
    cardType: null,
    isLoading: true,
  }

  componentDidMount() {
    let { data } = urlToObject(document.location.href)
    this.setState({ isLoading: false })
    try {
      data = JSON.parse(data)
    } catch (err) {
      return
    }
    this.data = data
  }

  sendError = error => {
    this.props.onError(error)
  }

  handleFillTestData = setValues => {
    if (process.env.NODE_ENV === 'development') {
      setValues(TEST_CARD)
    }
  }

  async handleFormSubmit(values, { setSubmitting, setFieldError }) {
    this.setState({
      isLoading: true,
      errorMessage: null,
    })

    try {
      const braintreeToken = await tokenizeCard({
        number: values.number.replace(/\s/g, ''),
        expirationDate: `${values.expirationMonth}/${values.expirationYear}`,
        cvv: values.cvv,
        cardHolder: values.cardHolder,
        cardType: this.state.cardType?.type,
        ...this.props.withZIP && { postalCode: values.postalCode },
      })
      setSubmitting(false)
      this.props.onTokenize(braintreeToken)
    } catch (err) {
      setSubmitting(false)
      console.error(err) //eslint-disable-line

      // Set errors per fields
      if (err.data && Array.isArray(err.data.errors)) {
        err.data.errors.forEach(({ field, message }) => setFieldError(field, message))
      }

      this.setState({ errorMessage: err.message })

      this.props.onError(err.message ? err.message : err)
    }
    this.setState({ isLoading: false })
  }

  validate(values) {
    const errors = {}

    this.setState({ cardType: null })

    let validation = cardValidator.number(values.number)
    if (!validation.isPotentiallyValid || !validation.isValid) {
      errors.number = this.props.isArabic ? 'بيانات غير صالحة' : 'Invalid data'
    }
    if (!values.number) {
      errors.number = this.props.isArabic ? 'حقل مطلوب' : 'Required field'
    }

    if (validation.card) {
      this.setState({ cardType: validation.card })
    }

    validation = cardValidator.expirationDate({
      year: values.expirationYear,
      month: values.expirationMonth,
    })
    if (!validation.isPotentiallyValid || !validation.isValid) {
      errors.expirationMonth = this.props.isArabic ? 'تاريخ غير صالح' : 'Invalid date'
    }

    validation = cardValidator.expirationMonth(values.expirationMonth)
    if (!validation.isPotentiallyValid || !validation.isValid) {
      errors.expirationMonth = this.props.isArabic ? 'تاريخ غير صالح' : 'Invalid month'
    }

    if (!values.expirationMonth) {
      errors.expirationMonth = this.props.isArabic ? 'حقل مطلوب' : 'Required field'
    }

    validation = cardValidator.expirationYear(values.expirationYear)
    if (!validation.isPotentiallyValid || !validation.isValid) {
      errors.expirationYear = this.props.isArabic ? 'تاريخ غير صالح' : 'Invalid year'
    }

    if (!values.expirationYear) {
      errors.expirationYear = this.props.isArabic ? 'حقل مطلوب' : 'Required field'
    }

    validation = cardValidator.cvv(
      values.cvv,
      this.state.cardType ? this.state.cardType.code.size : null,
    )
    if (!validation.isPotentiallyValid || !validation.isValid) {
      errors.cvv = this.props.isArabic ? 'حقل مطلوب' : 'Required field'
    }

    if (this.props.withZIP) {
      validation = cardValidator.postalCode(values.postalCode)
      if (!validation.isPotentiallyValid || !validation.isValid) {
        errors.postalCode = this.props.isArabic ? 'حقل مطلوب' : 'Required field'
      }
    }

    if (!/^[a-zA-Z-' ]+$/.test(values.cardHolder.trim())) {
      errors.cardHolder = this.props.isArabic ? 'بيانات غير صالحة' : 'Invalid data'
    }

    if (!values.cardHolder.trim().length) {
      errors.cardHolder = this.props.isArabic ? 'حقل مطلوب' : 'Required field'
    }

    if (Object.keys(errors).length) this.sendError(errors)

    return errors
  }

  // NOTE: hack for disable chrome autofill
  // https://stackoverflow.com/a/15917221/1044982
  renderFakeUserPass = () => (
    <>
      <input style={{ display: 'none' }} type="text" name="fakeusernameremembered" />
      <input style={{ display: 'none' }} type="password" name="fakepasswordremembered" />
    </>
  )

  render() {
    const { cardType } = this.state

    if (cardType) {
      this.fields.cvv.maxLength = cardType.code.size
      this.fields.cvv.placeholder = cardType.code.name
      if (cardType.code.name === 'CID') this.fields.cvv.toolTip = cidToolTip
      else this.fields.cvv.toolTip = cvvToolTip
    }

    const { ...frontCardFields } = this.fields
    return (
      <Formik
        ref={this.form}
        initialValues={{
          number: '',
          expirationMonth: '',
          expirationYear: '',
          cvv: '',
          postalCode: '',
          cardHolder: '',
        }}
        validate={values => this.validate(values)}
        onSubmit={(...args) => this.handleFormSubmit(...args)}
        render={({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setValues,
        }) => (
          <form
            onSubmit={handleSubmit}
            className={classNames('add-card', { 'is-disabled': isSubmitting })}
          >
            {(process.env.NODE_ENV === 'development') ? (
              <button
                type="button"
                className="add-card__test-data"
                onClick={() => this.handleFillTestData(setValues)}
              >test data
              </button>
            ) : null}
            {this.state.isSubmitting && <Spinner className="add-card__spinner" />}
            {this.renderFakeUserPass()}
            <div className="add-card__card-form">
              <div className="add-card__card-front">
                <div className="add-card__fields">
                  {cardType && (
                  <CreditCardIcon className="add-card__card-icon" big cardType={cardType.type} />
                  )}

                  {Object.keys(frontCardFields).map(key => {
                    const item = this.fields[key]
                    if (item.pair) {
                      item.pair.value = values[item.pair.name]
                      item.pair.error = errors[item.pair.name]
                    }

                    const fieldProps = {
                      className: `add-card__field add-card__field-${key.toLowerCase()} ${item.className}`,
                      touched: touched[key],
                      error: errors[key],
                      type: item.type,
                      name: key,
                      value: values[key],
                      placeholder: item.placeholder,
                      mask: item.mask,
                      maxLength: item.maxLength,
                      autocomplete: item.autocomplete,
                      pair: item.pair,
                      toolTip: item.toolTip,
                      handleChange,
                      handleBlur,
                    }

                    return <Field key={key} isErrorAbsolute {...fieldProps} />
                  })}
                </div>
              </div>
            </div>

            <div className="add-card__general-error">
              {this.state.errorMessage || this.props.submitError}
            </div>
            <Button
              className="add-card__submit"
              type="submit"
              isLoading={this.state.isLoading || this.props.isLoading}
            >
              <img src={lockIcon} alt="lock" />
              &nbsp;{this.props.isArabic ? 'حفظ بيانات البطاقة' : 'Save card'}
            </Button>
          </form>
        )}
      />
    )
  }
}

// NOTE: we make forward Ref for submit form outside
// eslint-disable-next-line
export default React.forwardRef((props, ref) => <AddCardForm {...props} forwardedRef={ref} />)
